// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Button,
  Dropdown,
  InputLabel,
  Radio,
  TextArea,
  TextInput,
  useToast,
} from '@loa/ui-components';
import type { DropdownOption } from '@loa/ui-components';
import type { Client } from '../../../types/client';
import type { Quote, QuoteProvider } from '../../../types/quote';
import { COVER_TYPE_LABELS } from '../../../types/quote';
import type {
  CreateQuoteBackedProposalRequest,
  PensionWizardBootstrapResult,
} from '../../../types/proposal';
import { brokerageConfigApi } from '../../../api/brokerageConfigApi';
import { proposalApi } from '../../../api/proposalApi';
import { quoteApi } from '../../../api/quoteApi';
import { useSoloClient } from '../../../hooks/useSoloClient';
import { useProductCatalogue } from '../../../hooks/useProductCatalogue';
import { ProviderMultiSelect } from '../../quotes/ProviderMultiSelect';
// Reuses the pension wizard's Step-1 chrome (`pension-wizard-step__*`).
import '../wizard/PensionProposalWizard.css';

const COVER_TYPE_OPTIONS: DropdownOption[] = Object.entries(COVER_TYPE_LABELS).map(
  ([value, text]) => ({ value, text }),
);

const TERM_MIN = 5;
const TERM_MAX = 40;
const TERM_OPTIONS: DropdownOption[] = Array.from(
  { length: TERM_MAX - TERM_MIN + 1 },
  (_, i) => {
    const years = TERM_MIN + i;
    return { value: String(years), text: `${years} years` };
  },
);

const BOTH_VALUE = '__BOTH__';

const parseAmount = (raw: string): number => {
  const cleaned = raw.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB');
};

export type ProtectionQuoteRequestStepProps = Readonly<{
  profileId: string;
  brokerageId: string;
  clients: Client[];
  /**
   * When provided, the form renders in edit mode against this quote — the
   * target client(s) are fixed (PRD RQ-9/FR-7 parity with pension) and
   * submit calls {@code quoteApi.updateQuote} instead of bootstrapping a new
   * quote-backed proposal.
   */
  existingQuote?: Quote;
  /** The proposal's target client id, used to pre-select the radio in edit mode. */
  targetClientId?: string;
  onBootstrapped: (result: PensionWizardBootstrapResult) => void;
  onQuoteUpdated?: (quote: Quote) => void;
  onCancel: () => void;
}>;

/**
 * Quote Request step for the Protection builder rail (Figma 1453-62447;
 * docs/specs/investment-protection-proposals-prd.md §1.1). On submit it
 * bootstraps the `PROTECTION` quote and the DRAFT `PROTECTION` proposal
 * shell in one backend transaction (`POST /api/proposals/quote-backed`), or
 * — in edit mode — updates the already-bootstrapped quote via the existing
 * `PUT /api/quotes/:id`.
 */
export function ProtectionQuoteRequestStep({
  profileId,
  brokerageId,
  clients,
  existingQuote,
  targetClientId: targetClientIdProp,
  onBootstrapped,
  onQuoteUpdated,
  onCancel,
}: ProtectionQuoteRequestStepProps) {
  const { showToast } = useToast();
  const isEditMode = Boolean(existingQuote);
  const { isSolo, soloClient } = useSoloClient(clients);
  const { options: protectionTypeOptions } = useProductCatalogue('INSURANCE');

  // The "Who is the quote for?" radio carries either a single client id or
  // the BOTH_VALUE sentinel meaning "the profile's first two clients".
  const [quoteForChoice, setQuoteForChoice] = useState<string>(
    targetClientIdProp ?? (isSolo ? soloClient?.id ?? '' : ''),
  );
  const [protectionType, setProtectionType] = useState(existingQuote?.protectionType ?? '');
  const [coverType, setCoverType] = useState(existingQuote?.coverType ?? '');
  const [amount, setAmount] = useState(existingQuote ? String(existingQuote.amount) : '');
  const [providerOptions, setProviderOptions] = useState<QuoteProvider[]>([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>(
    existingQuote?.sentToProviders.map((p) => p.id) ?? [],
  );
  const [termOfCover, setTermOfCover] = useState(
    existingQuote?.termOfCover != null ? String(existingQuote.termOfCover) : '',
  );
  const [specialRequests, setSpecialRequests] = useState(existingQuote?.specialRequests ?? '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!brokerageId) return;
    let cancelled = false;
    brokerageConfigApi
      .getEnabledProviders(brokerageId)
      .then((data) => {
        if (cancelled) return;
        setProviderOptions(
          data.content.map((bp) => ({
            id: bp.provider.id,
            name: bp.provider.name,
            logoUrl: bp.provider.logoUrl,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setProviderOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [brokerageId]);

  // Solo relationships preselect the only possible recipient (PRD RQ-9) so
  // the read-only DOB fields populate without an extra click.
  useEffect(() => {
    if (!quoteForChoice && isSolo && soloClient) {
      setQuoteForChoice(soloClient.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSolo, soloClient]);

  const isBothChoice = quoteForChoice === BOTH_VALUE;
  const showBoth = !isSolo && clients.length >= 2;

  const primaryClient = useMemo(() => {
    if (isBothChoice) return clients[0];
    return clients.find((c) => c.id === quoteForChoice);
  }, [clients, quoteForChoice, isBothChoice]);
  const secondaryClient = useMemo(
    () => (isBothChoice ? clients[1] : undefined),
    [clients, isBothChoice],
  );

  const clientDobDisplay = formatDate(primaryClient?.dateOfBirth ?? existingQuote?.clientDob);
  const clientDobSecondaryDisplay = formatDate(
    secondaryClient?.dateOfBirth ?? existingQuote?.clientDobSecondary,
  );

  const termNumber = Number(termOfCover);
  const termValid = Number.isFinite(termNumber) && termNumber >= TERM_MIN && termNumber <= TERM_MAX;
  const amountValid = parseAmount(amount) > 0;
  const canSubmit =
    Boolean(quoteForChoice) &&
    Boolean(protectionType) &&
    Boolean(coverType) &&
    amountValid &&
    selectedProviderIds.length >= 1 &&
    termValid;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      if (isEditMode && existingQuote) {
        const updated = await quoteApi.updateQuote(existingQuote.id, {
          clientNames: existingQuote.clientNames,
          amount: parseAmount(amount),
          currencyCode: existingQuote.currencyCode,
          clientDob: existingQuote.clientDob,
          clientDobSecondary: existingQuote.clientDobSecondary,
          protectionType,
          coverType: coverType as Quote['coverType'],
          termOfCover: termNumber,
          specialRequests: specialRequests.trim() || undefined,
          providerIds: selectedProviderIds,
        });
        showToast('Quote request updated', 'success');
        onQuoteUpdated?.(updated);
        return;
      }

      const request: CreateQuoteBackedProposalRequest = {
        profileId,
        targetClientId: isBothChoice ? clients[0]?.id ?? '' : quoteForChoice,
        proposalType: 'PROTECTION',
        secondaryClientId: isBothChoice ? clients[1]?.id : undefined,
        amount: parseAmount(amount),
        currencyCode: 'EUR',
        providerIds: selectedProviderIds,
        specialRequests: specialRequests.trim() || undefined,
        protectionType,
        coverType,
        termOfCover: termNumber,
      };
      const result = await proposalApi.startQuoteBackedProposal(request);
      showToast('Quote requested', 'success');
      onBootstrapped(result);
    } catch (err: unknown) {
      const fallback = isEditMode ? 'Failed to update quote request' : 'Failed to request quotes';
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ?? fallback
        : fallback;
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pension-wizard-step" data-testid="wizard-step-quote-request">
      <h2 className="pension-wizard-step__title">New Protection Quote</h2>
      <div className="flex flex-col gap-lg">
        {!isSolo && (
          <div className="flex flex-col gap-md">
            <InputLabel required>Who is the quote for?</InputLabel>
            <div className="flex flex-col gap-sm">
              {clients.map((client) => (
                <Radio
                  key={client.id}
                  name="protection-quote-target-client"
                  label={`${client.firstName} ${client.lastName}`}
                  checked={quoteForChoice === client.id}
                  disabled={isEditMode}
                  onChange={() => setQuoteForChoice(client.id)}
                />
              ))}
              {showBoth && (
                <Radio
                  name="protection-quote-target-client"
                  label="Both"
                  checked={isBothChoice}
                  disabled={isEditMode}
                  onChange={() => setQuoteForChoice(BOTH_VALUE)}
                />
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-lg gap-y-lg">
          <Dropdown
            label="Protection Type"
            placeholder="Select a protection type"
            options={protectionTypeOptions}
            value={protectionType}
            onChange={(v) => setProtectionType(v)}
            required
            fluid
          />

          <Dropdown
            label="Cover Type"
            placeholder="Select a cover type"
            options={COVER_TYPE_OPTIONS}
            value={coverType}
            onChange={(v) => setCoverType(v)}
            required
            fluid
          />

          <TextInput
            label="Amount"
            placeholder="€0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            fluid
          />

          <ProviderMultiSelect
            label="Request quote from"
            options={providerOptions}
            selectedIds={selectedProviderIds}
            onChange={setSelectedProviderIds}
          />

          <TextInput label="Client DOB" value={clientDobDisplay} disabled readOnly fluid />

          {isBothChoice && (
            <TextInput
              label="Secondary client DOB"
              value={clientDobSecondaryDisplay}
              disabled
              readOnly
              fluid
            />
          )}

          <Dropdown
            label="Term of Cover"
            placeholder="Select a term"
            options={TERM_OPTIONS}
            value={termOfCover}
            onChange={(v) => setTermOfCover(v)}
            required
            fluid
          />

          <TextArea
            label="Special requests"
            placeholder="e.g. NIL exit penalties; client recently relocated; advisor notes…"
            value={specialRequests}
            onChange={(e) => setSpecialRequests(e.target.value)}
            rows={4}
            fluid
          />
        </div>
      </div>

      <div className="pension-wizard-step__footer">
        <Button variant="outlined" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="filled"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          loading={submitting}
        >
          {isEditMode ? 'Save Changes' : 'Get Quotes'}
        </Button>
      </div>
    </div>
  );
}

export default ProtectionQuoteRequestStep;
